import { inject, injectable, injectFromBase } from 'inversify'
import { DomainSettings } from '@/decorators/domainDecorators'
import DomainBaseService from '@/services/domain/domainBaseService'
import TollFreeBrand from '@/data/models/domain/services/regulations/TollFreeBrand'
import type TollFreeBrandRepository from '@/data/repositories/domain/services/regulations/tollFreeBrandRepository'
import { getStatusColor, type MapStatusColor } from '@/utils/status'
import { SERVICE_TYPES } from '@/core/container/types'
import type EntityManagerService from '@/data/repositories/entityManagerService'
import type ModelSubscriptionService from '@/services/transport/modelSubscriptionService'
import type PreloaderManager from '@/services/preloaders/preloaderManager'
import type TranslateService from '@/services/translateService'
import type { StatusCellProps } from '@/services/domain/types'
import { TendDlcBrandVerticalType } from '@/services/domain/services/regulations/tenDlcBrand/types'
import { TollFreeRequestStatus } from '@/services/domain/services/regulations/tollFreeRequest/types'

@DomainSettings({
  model: TollFreeBrand,
})
@injectable()
@injectFromBase()
export default class TollFreeBrandService extends DomainBaseService<TollFreeBrandRepository> {
  constructor(
    @inject(SERVICE_TYPES.EntityManager) protected readonly entityManager: EntityManagerService,
    @inject(SERVICE_TYPES.ModelSubscriptionService) protected readonly subscription: ModelSubscriptionService,
    @inject(SERVICE_TYPES.PreloaderManager) protected readonly preloaderManager: PreloaderManager,
    @inject(SERVICE_TYPES.TranslateService) protected readonly translateService: TranslateService,
  ) {
    super(entityManager, subscription, preloaderManager)
  }

  public getStatus(status: TollFreeRequestStatus | TollFreeRequestStatus): StatusCellProps {
    const labelMap: Record<TollFreeRequestStatus, string> = {
      [TollFreeRequestStatus.DRAFT]: this.translateService.t('status.tenDlcBrandStatus.draft'),
      [TollFreeRequestStatus.PENDING]: this.translateService.t('status.tenDlcBrandStatus.pending'),
      [TollFreeRequestStatus.IN_REVIEW]: this.translateService.t('status.tenDlcBrandStatus.inReview'),
      [TollFreeRequestStatus.VERIFIED]: this.translateService.t('status.tenDlcBrandStatus.verified'),
      [TollFreeRequestStatus.ACTION_REQUIRED]: this.translateService.t('status.tenDlcBrandStatus.actionRequired'),
    }
    const colorMap: MapStatusColor<TollFreeRequestStatus> = {
      gray: [TollFreeRequestStatus.DRAFT],
      green: [TollFreeRequestStatus.VERIFIED],
      blue: [TollFreeRequestStatus.PENDING, TollFreeRequestStatus.IN_REVIEW],
      orange: [TollFreeRequestStatus.ACTION_REQUIRED],
    }

    return {
      value: labelMap[status] || '',
      color: getStatusColor<TollFreeRequestStatus | TollFreeRequestStatus>(colorMap, status),
      outline: status !== TollFreeRequestStatus.ACTION_REQUIRED,
    }
  }

  public getVerticalTypeLabel(verticalType: TendDlcBrandVerticalType) {
    const map: Record<TendDlcBrandVerticalType, string> = {
      [TendDlcBrandVerticalType.UNKNOWN]: this.translateService.t('common.unknown'),
      [TendDlcBrandVerticalType.REAL_ESTATE]: this.translateService.t('tenDlcVerticalType.realEstate'),
      [TendDlcBrandVerticalType.HEALTHCARE]: this.translateService.t('tenDlcVerticalType.healthcare'),
      [TendDlcBrandVerticalType.ENERGY]: this.translateService.t('tenDlcVerticalType.energy'),
      [TendDlcBrandVerticalType.ENTERTAINMENT]: this.translateService.t('tenDlcVerticalType.entertainment'),
      [TendDlcBrandVerticalType.RETAIL]: this.translateService.t('tenDlcVerticalType.retail'),
      [TendDlcBrandVerticalType.AGRICULTURE]: this.translateService.t('tenDlcVerticalType.agriculture'),
      [TendDlcBrandVerticalType.INSURANCE]: this.translateService.t('tenDlcVerticalType.insurance'),
      [TendDlcBrandVerticalType.EDUCATION]: this.translateService.t('tenDlcVerticalType.education'),
      [TendDlcBrandVerticalType.HOSPITALITY]: this.translateService.t('tenDlcVerticalType.hospitality'),
      [TendDlcBrandVerticalType.FINANCIAL]: this.translateService.t('tenDlcVerticalType.financial'),
      [TendDlcBrandVerticalType.GAMBLING]: this.translateService.t('tenDlcVerticalType.gambling'),
      [TendDlcBrandVerticalType.CONSTRUCTION]: this.translateService.t('tenDlcVerticalType.construction'),
      [TendDlcBrandVerticalType.NGO]: this.translateService.t('tenDlcVerticalType.ngo'),
      [TendDlcBrandVerticalType.MANUFACTURING]: this.translateService.t('tenDlcVerticalType.manufacturing'),
      [TendDlcBrandVerticalType.GOVERNMENT]: this.translateService.t('tenDlcVerticalType.government'),
      [TendDlcBrandVerticalType.TECHNOLOGY]: this.translateService.t('tenDlcVerticalType.technology'),
      [TendDlcBrandVerticalType.COMMUNICATION]: this.translateService.t('tenDlcVerticalType.communication'),
      [TendDlcBrandVerticalType.POSTAL]: this.translateService.t('tenDlcVerticalType.postal'),
      [TendDlcBrandVerticalType.TRANSPORTATION]: this.translateService.t('tenDlcVerticalType.transportation'),
    }

    return map[verticalType]
  }
}
